import type { RootState } from '@/src/store'
import type { Gyg365FranchiseList } from '@/src/types/apps/gyg365FranchiseListTypes'
import { ROUTES } from '@/src/utils/routes'
import { isRestaurantMasterListRoute } from './bhyveNavigationVisibility'

export type BhyveAppCrumb = {
  label: string
  href?: string
  showRestaurantStatus?: boolean
  showFranchiseStatus?: boolean
}

/** First crumb on all BHYBE header trails — master restaurant list is the app landing. */
const HOME_LANDING_CRUMB: BhyveAppCrumb = { label: 'Home', href: ROUTES.RESTAURANT }

function humanizeSegment(segment: string): string {
  return segment.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
}

/**
 * URL segments after a section root (e.g. `/app/marketing-portal` + `/…/nutritional-info` → `Nutritional Info`).
 * Pass real browser path (`asPath` without query) via `pathForSegments` so dynamic routes use real ids, not `[id]`.
 * Intermediate crumbs link to each level; the last crumb has no `href` (current page).
 */
function getSectionSubPathCrumbs(sectionRoot: string, pathForSegments: string): BhyveAppCrumb[] {
  const root = sectionRoot.replace(/\/$/, '')
  const path = (pathForSegments.split('?')[0] ?? pathForSegments).replace(/\/$/, '')
  if (path === root) return []
  if (!path.startsWith(`${root}/`)) return []

  const rest = path.slice(root.length + 1)
  const segments = rest.split('/').filter(Boolean)
  if (segments.length === 0) return []

  const crumbs: BhyveAppCrumb[] = []
  for (let i = 0; i < segments.length; i++) {
    const seg = segments[i]!
    const label = humanizeSegment(seg)
    const isLast = i === segments.length - 1
    const hrefPath = `${root}/${segments.slice(0, i + 1).join('/')}`

    /** Primary nav group only — not a route (see `Admin Tools` in gyg-navigation). */
    const isNonNavigableAppFolder = root === '/app' && seg === 'admin-tools'

    if (isLast || isNonNavigableAppFolder) {
      crumbs.push({ label })
    } else {
      crumbs.push({ label, href: hrefPath })
    }
  }

  return crumbs
}

const RESTAURANT_DYNAMIC_ID_BASE = '/app/restaurant/[id]'

/** Longest-prefix wins; labels mirror secondary nav (see `SubNavigation`). */
type RestaurantTailRule = { prefix: string; labels: string[] }

const RESTAURANT_TAIL_RULES: RestaurantTailRule[] = [
  {
    prefix: `${RESTAURANT_DYNAMIC_ID_BASE}/menu-services/equipment/addEditContact`,
    labels: ['Menu & Services', 'Equipment', 'Contact']
  },
  {
    prefix: `${RESTAURANT_DYNAMIC_ID_BASE}/menu-services/equipment/addEdit`,
    labels: ['Menu & Services', 'Equipment']
  },
  {
    prefix: `${RESTAURANT_DYNAMIC_ID_BASE}/menu-services/menu-management`,
    labels: ['Menu & Services', 'Menu Management']
  },
  {
    prefix: `${RESTAURANT_DYNAMIC_ID_BASE}/menu-services/trading-hours/v3`,
    labels: ['Menu & Services', 'Trading Hours', 'V3']
  },
  {
    prefix: `${RESTAURANT_DYNAMIC_ID_BASE}/menu-services/trading-hours`,
    labels: ['Menu & Services', 'Trading Hours']
  },
  {
    prefix: `${RESTAURANT_DYNAMIC_ID_BASE}/menu-services/order-offset`,
    labels: ['Menu & Services', 'Order offset']
  },
  {
    prefix: `${RESTAURANT_DYNAMIC_ID_BASE}/menu-services/equipment`,
    labels: ['Menu & Services', 'Equipment']
  },
  {
    prefix: `${RESTAURANT_DYNAMIC_ID_BASE}/history-log`,
    labels: ['Menu & Services', 'History Log']
  },
  {
    prefix: `${RESTAURANT_DYNAMIC_ID_BASE}/ready-to-roll/add/submission/[daypart]`,
    labels: ['Operations', 'Ready to Roll?', 'Submission']
  },
  {
    prefix: `${RESTAURANT_DYNAMIC_ID_BASE}/ready-to-roll/add`,
    labels: ['Operations', 'Ready to Roll?']
  },
  {
    prefix: `${RESTAURANT_DYNAMIC_ID_BASE}/ready-to-roll/view/[slug]`,
    labels: ['Operations', 'Ready to Roll?', 'View']
  },
  {
    prefix: `${RESTAURANT_DYNAMIC_ID_BASE}/ready-to-roll`,
    labels: ['Operations', 'Ready to Roll?']
  },
  {
    prefix: `${RESTAURANT_DYNAMIC_ID_BASE}/rss/view/[slug]`,
    labels: ['Operations', 'Cleaning Schedule', 'View']
  },
  {
    prefix: `${RESTAURANT_DYNAMIC_ID_BASE}/rss/addTasks`,
    labels: ['Operations', 'Cleaning Schedule', 'Tasks']
  },
  {
    prefix: `${RESTAURANT_DYNAMIC_ID_BASE}/rss/add`,
    labels: ['Operations', 'Cleaning Schedule']
  },
  {
    prefix: `${RESTAURANT_DYNAMIC_ID_BASE}/rss`,
    labels: ['Operations', 'Cleaning Schedule']
  },
  {
    prefix: `${RESTAURANT_DYNAMIC_ID_BASE}/training-dashboard/trainee/[traineeId]`,
    labels: ['Operations', 'Training Plans', 'Trainee']
  },
  {
    prefix: `${RESTAURANT_DYNAMIC_ID_BASE}/training-dashboard`,
    labels: ['Operations', 'Training Plans']
  },
  {
    prefix: `${RESTAURANT_DYNAMIC_ID_BASE}/end-of-night`,
    labels: ['Operations', 'End of Night']
  },
  {
    prefix: `${RESTAURANT_DYNAMIC_ID_BASE}/kitchen-management/oms-lines`,
    labels: ['Kitchen Management', 'OMS Lines']
  },
  {
    prefix: `${RESTAURANT_DYNAMIC_ID_BASE}/kitchen-management/oms-printers`,
    labels: ['Kitchen Management', 'OMS Printers']
  },
  {
    prefix: `${RESTAURANT_DYNAMIC_ID_BASE}/kitchen-management/oms-order-types`,
    labels: ['Kitchen Management', 'OMS Order Types']
  },
  {
    prefix: `${RESTAURANT_DYNAMIC_ID_BASE}/report-training-planner`,
    labels: ['Reporting', 'Training Planner']
  },
  {
    prefix: `${RESTAURANT_DYNAMIC_ID_BASE}/report-ready-to-roll`,
    labels: ['Reporting', 'Ready to Roll?']
  },
  {
    prefix: `${RESTAURANT_DYNAMIC_ID_BASE}/report-rss`,
    labels: ['Reporting', 'Cleaning Schedule']
  },
  {
    prefix: `${RESTAURANT_DYNAMIC_ID_BASE}/reporting`,
    labels: ['Reporting']
  },
  {
    prefix: `${RESTAURANT_DYNAMIC_ID_BASE}/calendar`,
    labels: ['Calendar']
  }
]

function getRestaurantScopedTailCrumbs(pathname: string): BhyveAppCrumb[] {
  if (pathname !== RESTAURANT_DYNAMIC_ID_BASE && !pathname.startsWith(`${RESTAURANT_DYNAMIC_ID_BASE}/`)) {
    return []
  }

  if (pathname === RESTAURANT_DYNAMIC_ID_BASE || pathname === `${RESTAURANT_DYNAMIC_ID_BASE}/`) {
    return []
  }

  const sortedRules = [...RESTAURANT_TAIL_RULES].sort((a, b) => b.prefix.length - a.prefix.length)

  for (const rule of sortedRules) {
    if (pathname === rule.prefix || pathname.startsWith(`${rule.prefix}/`)) {
      const crumbs: BhyveAppCrumb[] = rule.labels.map(label => ({ label }))
      const rest = pathname.length > rule.prefix.length ? pathname.slice(rule.prefix.length).replace(/^\//, '') : ''

      if (rest) {
        const extraLabels = rest
          .split('/')
          .filter(Boolean)
          .filter(seg => !seg.startsWith('['))
          .map(seg => ({ label: humanizeSegment(seg) }))

        return [...crumbs, ...extraLabels]
      }

      return crumbs
    }
  }

  const tailPart = pathname.slice(`${RESTAURANT_DYNAMIC_ID_BASE}/`.length)
  if (!tailPart) return []

  return tailPart
    .split('/')
    .filter(Boolean)
    .filter(seg => !seg.startsWith('['))
    .map(seg => ({ label: humanizeSegment(seg) }))
}

const CUSTOMER_PORTAL_ROOT = '/app/customer-portal'

/** Pathname templates use Next `[param]` segments (same as filesystem routes). */
type CustomerPortalTailRule = { prefix: string; labels: string[] }

const CUSTOMER_PORTAL_TAIL_RULES: CustomerPortalTailRule[] = [
  { prefix: `${CUSTOMER_PORTAL_ROOT}/users/[id]`, labels: ['Users', 'Profile'] },
  { prefix: `${CUSTOMER_PORTAL_ROOT}/orders/[id]`, labels: ['Orders', 'Details'] },
  { prefix: `${CUSTOMER_PORTAL_ROOT}/users`, labels: ['Users'] }
]

function getCustomerPortalTailCrumbs(pathname: string): BhyveAppCrumb[] {
  if (!pathname.startsWith(`${CUSTOMER_PORTAL_ROOT}/`) && pathname !== CUSTOMER_PORTAL_ROOT) {
    return []
  }

  if (pathname === CUSTOMER_PORTAL_ROOT || pathname === `${CUSTOMER_PORTAL_ROOT}/`) {
    return []
  }

  const sortedRules = [...CUSTOMER_PORTAL_TAIL_RULES].sort((a, b) => b.prefix.length - a.prefix.length)

  for (const rule of sortedRules) {
    if (pathname === rule.prefix || pathname.startsWith(`${rule.prefix}/`)) {
      const crumbs: BhyveAppCrumb[] = rule.labels.map(label => ({ label }))
      const rest = pathname.length > rule.prefix.length ? pathname.slice(rule.prefix.length).replace(/^\//, '') : ''

      if (rest) {
        const extraLabels = rest
          .split('/')
          .filter(Boolean)
          .filter(seg => !seg.startsWith('['))
          .map(seg => ({ label: humanizeSegment(seg) }))

        return [...crumbs, ...extraLabels]
      }

      return crumbs
    }
  }

  const tailPart = pathname.slice(`${CUSTOMER_PORTAL_ROOT}/`.length)
  if (!tailPart) return []

  return tailPart
    .split('/')
    .filter(Boolean)
    .filter(seg => !seg.startsWith('['))
    .map(seg => ({ label: humanizeSegment(seg) }))
}

type RestaurantRecord =
  | NonNullable<RootState['gyg365RestoList']['restoDataWithUsers']>
  | NonNullable<RootState['gyg365RestoList']['restoData']>

type FranchiseRecord = Gyg365FranchiseList | undefined | null

export function buildBhyveAppCrumbs(args: {
  pathname: string

  /** Real URL without query — used for sub-path crumbs on nested routes (defaults to `pathname`). */
  pathForSegments?: string
  idParam: string | string[] | undefined
  restaurantRecord: RestaurantRecord | null | undefined
  franchiseRecord?: FranchiseRecord
}): BhyveAppCrumb[] {
  const { pathname, idParam, restaurantRecord, franchiseRecord } = args
  const pathForSegments = args.pathForSegments ?? pathname

  if (isRestaurantMasterListRoute(pathname)) {
    return []
  }

  if (pathname === ROUTES.RESTAURANT || pathname.startsWith(`${ROUTES.RESTAURANT}/`)) {
    const restaurantSectionBase: BhyveAppCrumb[] = [HOME_LANDING_CRUMB]

    if (pathname === '/app/restaurant/add') {
      return [...restaurantSectionBase, { label: 'Add' }]
    }

    if (pathname === '/app/restaurant/allocation') {
      return [...restaurantSectionBase, { label: 'Allocation & Delegation' }]
    }

    const id = typeof idParam === 'string' ? idParam : undefined
    if (id) {
      const matchesRoute = restaurantRecord != null && String(restaurantRecord.id) === String(id)
      const name = matchesRoute
        ? String(
            restaurantRecord.restaurantName ??
              restaurantRecord.fullRestaurantName ??
              ('name' in restaurantRecord ? (restaurantRecord as { name?: string }).name : undefined) ??
              'Restaurant'
          )
        : 'Restaurant'

      const tail = getRestaurantScopedTailCrumbs(pathname)

      return [...restaurantSectionBase, { label: name, showRestaurantStatus: matchesRoute }, ...tail]
    }

    return []
  }

  if (pathname === CUSTOMER_PORTAL_ROOT || pathname.startsWith(`${CUSTOMER_PORTAL_ROOT}/`)) {
    const portalBase: BhyveAppCrumb[] = [
      HOME_LANDING_CRUMB,
      { label: 'Customer Support', href: ROUTES.CUSTOMER_PORTAL }
    ]

    return [...portalBase, ...getCustomerPortalTailCrumbs(pathname)]
  }

  if (pathname === '/app/products' || pathname.startsWith('/app/products/')) {
    return [
      HOME_LANDING_CRUMB,
      { label: 'Products', href: ROUTES.PRODUCTS },
      ...getSectionSubPathCrumbs(ROUTES.PRODUCTS, pathForSegments)
    ]
  }

  if (pathname === '/app/franchise-hub' || pathname.startsWith('/app/franchise-hub/')) {
    const hubRoot = ROUTES.FRANCHISE_HUB

    if (pathname === '/app/franchise-hub/[id]') {
      const fid = typeof idParam === 'string' ? idParam : undefined
      const record = franchiseRecord
      const matches = Boolean(fid && record != null && record.id != null && String(record.id) === String(fid))
      const name = matches ? String(record!.name) : 'Franchise'

      return [HOME_LANDING_CRUMB, { label: 'Franchise', href: hubRoot }, { label: name, showFranchiseStatus: matches }]
    }

    return [
      HOME_LANDING_CRUMB,
      { label: 'Franchise', href: hubRoot },
      ...getSectionSubPathCrumbs(hubRoot, pathForSegments)
    ]
  }

  if (pathname === '/app/user-management' || pathname.startsWith('/app/user-management/')) {
    return [
      HOME_LANDING_CRUMB,
      { label: 'User management', href: ROUTES.USER_MANAGEMENT },
      ...getSectionSubPathCrumbs(ROUTES.USER_MANAGEMENT, pathForSegments)
    ]
  }

  if (pathname === '/app/order-management' || pathname.startsWith('/app/order-management/')) {
    return [
      HOME_LANDING_CRUMB,
      { label: 'Order management', href: ROUTES.ORDER_MANAGEMENT },
      ...getSectionSubPathCrumbs(ROUTES.ORDER_MANAGEMENT, pathForSegments)
    ]
  }

  if (pathname === '/app/marketing-portal' || pathname.startsWith('/app/marketing-portal/')) {
    return [
      HOME_LANDING_CRUMB,
      { label: 'Marketing', href: ROUTES.MARKETING_PORTAL },
      ...getSectionSubPathCrumbs(ROUTES.MARKETING_PORTAL, pathForSegments)
    ]
  }

  if (
    pathname === '/app/restaurant-menu-equipments/permanent-disable-requests' ||
    pathname.startsWith('/app/restaurant-menu-equipments/permanent-disable-requests/')
  ) {
    return [HOME_LANDING_CRUMB, { label: 'Pending Approvals' }, { label: 'Permanent Disable Requests' }]
  }

  if (pathname === '/app/submissions' || pathname.startsWith('/app/submissions/')) {
    return [
      HOME_LANDING_CRUMB,
      { label: 'Submissions', href: ROUTES.SUBMISSIONS },
      ...getSectionSubPathCrumbs(ROUTES.SUBMISSIONS, pathForSegments)
    ]
  }

  if (pathname === '/app/faq' || pathname.startsWith('/app/faq/')) {
    return [
      HOME_LANDING_CRUMB,
      { label: 'FAQ', href: ROUTES.FAQ },
      ...getSectionSubPathCrumbs(ROUTES.FAQ, pathForSegments)
    ]
  }

  const parts = pathname
    .replace(/^\/app\//, '')
    .split('/')
    .filter(Boolean)
  if (parts.length > 0) {
    return [HOME_LANDING_CRUMB, ...getSectionSubPathCrumbs('/app', pathForSegments)]
  }

  return [HOME_LANDING_CRUMB]
}

/** Short title for collapsed primary nav (bottom bar); aligns with breadcrumb semantics. */
export function getBhyvePrimaryContextTitle(args: {
  pathname: string
  pathForSegments?: string
  idParam: string | string[] | undefined
  restaurantRecord: RestaurantRecord | null | undefined
  franchiseRecord?: FranchiseRecord
}): string {
  const { pathname } = args

  if (isRestaurantMasterListRoute(pathname)) {
    return 'Master Restaurant List'
  }

  const crumbs = buildBhyveAppCrumbs(args)
  if (crumbs.length > 0) {
    return crumbs[crumbs.length - 1]?.label ?? 'GYG Admin'
  }

  const parts = pathname
    .replace(/^\/app\//, '')
    .split('/')
    .filter(Boolean)

  return parts.length > 0 ? humanizeSegment(parts[0]) : 'GYG Admin'
}
